<template>
  <div class="gi-root resizable-columns">

    <div class="gi-table-label-row">
      <div class="gi-table-label">DRAFT INVOICES</div>
      <b-button size="sm" variant="outline-primary" :disabled="!total || exportingExcel" @click="downloadExcel">
        <b-spinner v-if="exportingExcel" small class="mr-1" />
        <b-icon v-else icon="download" class="mr-1" />Download Excel
      </b-button>
    </div>

    <!-- Selection action bar -->
    <div class="gi-action-bar" v-if="modelValue.length">
      <span class="gi-action-bar__count">{{ modelValue.length }} selected</span>
      <div class="gi-action-bar__buttons">
        <b-button size="sm" variant="primary" :disabled="exportState.bulkExporting.value" @click="exportState.exportSelected(selectedDraftObjects, 'xls')">↓ XLS</b-button>
        <b-button size="sm" variant="primary" :disabled="exportState.bulkExporting.value" @click="exportState.exportSelected(selectedDraftObjects, 'pdf')">↓ PDF</b-button>
        <b-button size="sm" variant="primary" class="gi-approve-btn" :disabled="!!approving || hasMissingRequirements" @click="approveSelected">
          <b-spinner v-if="approving" small class="mr-1" />
          <b-icon v-else icon="check2" class="mr-1" />
          Approve
          <span ref="approveInfoIcon" class="gi-info-icon ml-1" @click.stop><b-icon icon="info-circle" /></span>
        </b-button>
        <b-tooltip :target="() => $refs.approveInfoIcon" placement="top" triggers="hover focus">
          {{ hasMissingRequirements ? `Blocked: missing ${missingRequirementsSummary}` : 'Approve for Auto Dispatch' }}
        </b-tooltip>
        <b-button size="sm" variant="warning" :disabled="exportState.bulkExporting.value || !!approving" @click="clearSelection">✕ Clear</b-button>
      </div>
    </div>

    <!-- Bulk export progress bar -->
    <div class="gi-progress-bar" v-if="exportState.bulkExporting.value">
      <div class="gi-progress-bar__label">
        Generating {{ exportState.bulkExportFormat.value === 'pdf' ? 'PDFs' : 'spreadsheets' }}…
        {{ exportState.bulkExportDone.value }} of {{ exportState.bulkExportTotal.value }} complete
      </div>
      <div class="gi-progress-bar__track">
        <div class="gi-progress-bar__fill" :style="{ width: exportState.bulkExportPct.value + '%' }"></div>
      </div>
    </div>

    <!-- Generating PDF/XLS after approval -->
    <div class="gi-progress-bar" v-if="exportState.docsUploading.value">
      <div class="gi-progress-bar__label">
        Generating invoice PDF/XLS… {{ exportState.docsUploadDone.value }} of {{ exportState.docsUploadTotal.value }} complete
      </div>
      <div class="gi-progress-bar__track">
        <div class="gi-progress-bar__fill" :style="{ width: (exportState.docsUploadTotal.value ? Math.round(exportState.docsUploadDone.value / exportState.docsUploadTotal.value * 100) : 0) + '%' }"></div>
      </div>
    </div>

    <div v-if="!loaded" class="gi-loading">Loading pending invoices…</div>
    <div v-else-if="draftError" class="gi-error">{{ draftError }}</div>
    <div v-else-if="totalUnfiltered === 0" class="gi-empty">No pending invoices found.</div>

    <!-- Filtered, searched, sorted and paged server-side (LimeIQ's drafts
         proxy, see _draft_listing) — only the current page is loaded. Kept
         mounted while hidden, since it is what fetches the rows. -->
    <BaseDataTable
      cancel-stale-requests
      v-show="loaded && !draftError && totalUnfiltered > 0"
      v-model="modelValue"
      ref="dataTableRef"
      :columns="GenerateInvoicesTableColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="100"
      :page-size-options="[100, 200, 500, 1000]"
      :tbody-tr-class="tableTbodyTrClass"
      :sticky-actions-column="false"
      selectable
    >
      <!-- Left side of the table header, opposite the search box. -->
      <template v-slot:header(left)>
        <b-form-radio-group
          v-model="billingTypeModel"
          :options="billingTypeOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          class="gi-billing-toggle"
          aria-label="Filter by billing type"
        />
      </template>

      <template v-slot:cell(invoice_ref)="{ item }">
        <span class="gi-ref">{{ exportState.generateRef(item) }}</span>
      </template>

      <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type || 'Market' }}</template>

      <template v-slot:cell(tpa)="{ item }">{{ item.tpa_name || '—' }}</template>
      <template v-slot:cell(underwriter)="{ item }">{{ item.underwriter_name || '—' }}</template>
      <template v-slot:cell(syndicate_number)="{ item }">{{ item.syndicate_number || '—' }}</template>
      <template v-slot:cell(currency_type)="{ item }">{{ item.currency_type }}</template>

      <template v-slot:cell(current_bdx_count)="{ item }">
        <span class="gi-num">{{ item.current_bdx_count }}</span>
      </template>

      <template v-slot:cell(previous_bdx_count)="{ item }">
        <span class="gi-num">{{ item.previous_bdx_count !== null ? item.previous_bdx_count : '—' }}</span>
      </template>

      <template v-slot:cell(bdx_variance)="{ item }">
        <span class="gi-num" :class="exportState.varianceClass(item.bdx_variance_alert)">
          <template v-if="item.bdx_variance !== null && item.bdx_variance !== undefined">
            {{ item.bdx_variance >= 0 ? '+' : '' }}{{ item.bdx_variance }}
          </template>
          <template v-else>—</template>
        </span>
      </template>

      <template v-slot:cell(amount_variance)="{ item }">
        <span class="gi-num" :class="exportState.varianceClass(item.amount_variance_alert)">
          <template v-if="item.amount_variance !== null && item.amount_variance !== undefined">
            {{ item.amount_variance >= 0 ? '+' : '' }}{{ exportState.formatCurrency(item.amount_variance, item.currency_type) }}
          </template>
          <template v-else>—</template>
        </span>
      </template>

      <template v-slot:cell(current_amount)="{ item }">
        <span class="gi-num gi-amount">{{ exportState.formatCurrency(item.current_amount, item.currency_type) }}</span>
      </template>

      <template v-slot:cell(billing_contact)="{ item }">
        <span class="gi-contact-cell">
          <span class="gi-contact" v-if="exportState.billingContactList(item).length">
            <span :title="exportState.billingContactList(item)[0]">{{ exportState.billingContactNames(item)[0] }}</span>
            <a
              v-if="exportState.billingContactList(item).length > 1"
              href
              class="gi-contact-more"
              @click.prevent="exportState.openBillingContactsModal(item)"
            >+{{ exportState.billingContactList(item).length - 1 }}</a>
          </span>
          <span v-else class="gi-alert-flag" title="TPA has no billing contact on file — this draft cannot be approved">—</span>
          <!-- LIQ-2710: every row opens the Billing Contacts popup, even with 0 or 1 contact -->
          <button
            type="button"
            class="gi-contact-menu"
            title="Billing contacts"
            aria-label="Billing contacts"
            @click.stop="exportState.openBillingContactsModal(item)"
          >
            <b-icon icon="three-dots" />
          </button>
        </span>
      </template>

      <template v-slot:cell(tpa_address)="{ item }">
        <span v-if="item.tpa_address" class="gi-contact gi-truncate-cell" :title="item.tpa_address">{{ firstAddressLine(item.tpa_address) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no address on file — this draft cannot be approved">— missing</span>
      </template>

      <template v-slot:cell(filevine_id)="{ item }">
        <span v-if="item.tpa_filevine_id" class="gi-contact gi-truncate-cell" :title="item.tpa_filevine_id">{{ truncateFilevine(item.tpa_filevine_id) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no FileVine Project Email on file — this draft cannot be approved">— missing</span>
      </template>

    </BaseDataTable>

    <!-- Billing Contacts Modal (LIQ-2710) -->
    <BillingContactsModal
      :show="exportState.showBillingContactsModal.value"
      :context="exportState.billingContactsModalContext.value"
      @hide="exportState.onBillingContactsModalHide"
      @changed="$emit('billing-contacts-changed', $event)"
    />

  </div>
</template>

<script>
import { isCancel } from 'axios'
import { defineComponent, ref, computed, watch, nextTick, onUpdated, getCurrentInstance } from '@vue/composition-api'

import { BaseDataTable } from '@components/base'
import { enableColumnResize, removeColumnResize } from '@components/base/base-data-table/useResizableColumns'

import InvoicingService from '@services/InvoicingService'
import { useInvoiceExport } from './useInvoiceExport'
import BillingContactsModal from './BillingContactsModal.vue'
import { getGenerateInvoicesTableColumns, DRAFT_BILLING_TYPE_OPTIONS, sortedFilterOptions } from './Generateinvoicetable.constants'
import { MULTI_VALUE_SEPARATOR } from './useInvoiceFilterOptions'
import { downloadTableExcel, toNumber } from './tableExcelExport'

// Draft groups sent per bulk-approve request — see approveSelected().
const BULK_APPROVE_BATCH_SIZE = 3

export default defineComponent({
  name: 'PendingInvoicesTable',

  components: {
    BillingContactsModal,
    BaseDataTable,
  },

  props: {
    // Market / LIMOSS / Both — owned by GenerateInvoicesTab (.sync) so the
    // summary cards above can follow it, and so it survives this table
    // being remounted after an approve.
    billingType: {
      type: String,
      default: 'Both',
    },
    // Latest Billing Contacts popup change, { companyId, emails }, relayed by
    // GenerateInvoicesTab from whichever table's popup made it (LIQ-2713).
    billingContactsUpdate: {
      type: Object,
      default: null,
    },
  },

  emits: ['changed', 'update:billingType', 'filtered', 'billing-contacts-changed'],

  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const proxy = instance.proxy

    const exportState = useInvoiceExport()

    // Follows the Market / LIMOSS / Both toggle — see
    // getGenerateInvoicesTableColumns for which columns each one hides.
    // Every draft row loaded so far, by id — what a selection made across
    // pages acts on (Approve / XLS / PDF), since only one page is loaded at
    // a time.
    const rowsById = ref({})
    // The multi-select filters offer every value across all drafts (sent by
    // the server with each page).
    const draftFilterOptions = ref({ tpa: [], underwriter: [], currency_type: [] })
    const GenerateInvoicesTableColumns = computed(() =>
      getGenerateInvoicesTableColumns(billingTypeFilter.value, draftFilterOptions.value))

    const draftError = ref(null)
    const total = ref(0)
    // Until the first page lands, and how many drafts exist before any
    // toggle/filter/search - for the loading and "none found" messages.
    const loaded = ref(false)
    const totalUnfiltered = ref(0)
    // The current filter/search/sort, without paging — what Download Excel
    // asks the server for.
    let lastListingParams = {}
    const exportingExcel = ref(false)

    const modelValue = ref([])
    const dataTableRef = ref(null)

    // Market / LIMOSS / Both toggle in the table's top-right (LIQ-2170).
    const billingTypeOptions = DRAFT_BILLING_TYPE_OPTIONS
    const billingTypeFilter = ref(props.billingType)
    watch(() => props.billingType, (value) => { billingTypeModel.value = value })

    // The toggle changes which header cells render, so the resize handles
    // injected into them have to come out first — synchronously, before
    // Vue re-renders the header (see removeColumnResize). onUpdated below
    // puts them back once the new columns are in place.
    const billingTypeModel = computed({
      get: () => billingTypeFilter.value,
      set: (value) => {
        if (value === billingTypeFilter.value) return
        removeColumnResize(instance.proxy.$el)
        billingTypeFilter.value = value
        emit('update:billingType', value)
      },
    })
    const billingTypeOf = (draft) => draft.billing_type || 'Market'

    // Whether a bulk approve is currently in flight — always dispatch_mode
    // 'auto' now that approval is a single action (no more Manual/Auto choice).
    const approving = ref(false)

    // ── Data loading ─────────────────────────────────────────────────────

    // A pending/draft "invoice" is just unlinked InvoiceCharge rows grouped
    // by (billing type, tpa, underwriter, syndicate, currency, month, year) —
    // Lincoln never assigns it a persisted invoice_id (that only happens on
    // approval), so BaseDataTable's `id` has to be a synthetic composite key.
    // A Market and a LIMOSS group can share every other field, and a LIMOSS
    // group splits further by syndicate (always null for Market).
    const draftKey = (draft) =>
      `${billingTypeOf(draft)}-${draft.tpa_company_id}-${draft.underwriter_company_id}-${draft.syndicate_number}-${draft.currency_type}-${draft.billing_month}-${draft.billing_year}`

    const withIds = (rows) => rows.map(d => ({ ...d, id: draftKey(d) }))

    const selectedDraftObjects = computed(() =>
      modelValue.value.map(id => rowsById.value[id]).filter(Boolean))

    // Shows only the first line of a TPA address for the Draft table's
    // Address column (LIQ-1859: "show the first line of the address") —
    // split on the first comma or newline. Full text is still available
    // via the cell's title attribute (native tooltip on hover).
    const firstAddressLine = (address) => {
      if (!address) return ''
      const firstLine = address.split(/[,\n]/)[0].trim()
      return firstLine || address.trim()
    }

    // Truncates a long FileVine value with an ellipsis for the Draft
    // table's FileVine column (LIQ-1859: e.g. "george.mccarthy@lim…").
    // The priority is flagging where it's missing, not showing the full
    // value, which stays available via the cell's title tooltip.
    const truncateFilevine = (value) => {
      if (!value) return ''
      return value.length > 18 ? `${value.slice(0, 18)}…` : value
    }

    // FileVine ID, billing contact, and address are all required on the
    // TPA's Company profile (billing contact lives on Members instead)
    // before a draft can be approved auto or manual. FileVine ID and Address
    // are TPA-only concepts — a LIMOSS draft has no TPA at all (it's grouped
    // by underwriter only), so neither applies; its billing contact is
    // always support@limeiq.com, resolved server-side (see
    // _enrich_with_billing_contacts), so that check never actually blocks it.
    const draftMissingRequirements = (draft) => {
      const missing = []
      if (draft.billing_type !== 'LIMOSS') {
        if (!draft.tpa_filevine_id) missing.push('FileVine Project Email')
        if (!draft.tpa_address) missing.push('Address')
      }
      if (!exportState.billingContactList(draft).length) missing.push('Billing Contact')
      return missing
    }

    const hasMissingRequirements = computed(() =>
      selectedDraftObjects.value.some(d => draftMissingRequirements(d).length > 0))

    const missingRequirementsSummary = computed(() => {
      const reasons = new Set()
      selectedDraftObjects.value.forEach(d => draftMissingRequirements(d).forEach(r => reasons.add(r)))
      return [...reasons].join(', ')
    })

    // ── BaseDataTable items provider ────────────────────────────────────
    // Filtered, searched, sorted and paged by LimeIQ's drafts proxy (see
    // _draft_listing there) — the same predicate behind the summary cards,
    // which it returns for the matching rows once a filter is in use.

    const listingParams = ({ filter, filters, sortBy, sortDesc }) => {
      const params = {
        billing_type_view: billingTypeFilter.value,
        search: filter || undefined,
        sort_by: sortBy || undefined,
        sort_desc: !!sortDesc,
      }
      ;(filters || []).forEach((f) => {
        // A multi-select column filter (LIQ-2656) is the list of picked values.
        if (Array.isArray(f.value)) {
          if (f.value.length) params[`${f.key}_in`] = f.value.join(MULTI_VALUE_SEPARATOR)
        } else if (f.value !== null && f.value !== undefined && f.value !== '') {
          params[f.key] = f.value
        }
      })
      return params
    }

    const fetchDrafts = async (params, signal) => {
      const resp = await InvoicingService.getDraftInvoices(params, { signal })
      return (resp.data && resp.data.data) || {}
    }

    const itemsProvider = async (context) => {
      const params = listingParams(context)
      lastListingParams = params
      draftError.value = null
      try {
        const inner = await fetchDrafts({ ...params, page: context.currentPage, page_size: context.perPage },
          context.signal)
        const rows = withIds(Array.isArray(inner.data) ? inner.data : [])
        total.value = inner.count || 0
        totalUnfiltered.value = inner.total_unfiltered || 0
        if (inner.filter_options) draftFilterOptions.value = sortedFilterOptions(inner.filter_options)
        rowsById.value = { ...rowsById.value, ...Object.fromEntries(rows.map(r => [r.id, r])) }
        // The cards' figures for every matching row (not just this page) —
        // null when no search/filter is in use, so the cards keep the
        // server's own summary.
        emit('filtered', { summary: inner.summary || null })
        return rows
      } catch (e) {
        // Superseded by a newer search/filter/page (see BaseDataTableTable.vue).
        if (isCancel(e)) return []
        console.error('Failed to load pending invoices:', e)
        draftError.value = 'Failed to load pending invoices.'
        total.value = 0
        emit('filtered', { summary: null })
        return []
      } finally {
        loaded.value = true
      }
    }

    const draftCellValue = (key, d) => ({
      invoice_ref: exportState.generateRef(d),
      billing_type: billingTypeOf(d),
      tpa: d.tpa_name,
      underwriter: d.underwriter_name,
      syndicate_number: d.syndicate_number,
      currency_type: d.currency_type,
      current_bdx_count: toNumber(d.current_bdx_count),
      previous_bdx_count: toNumber(d.previous_bdx_count),
      bdx_variance: toNumber(d.bdx_variance),
      amount_variance: toNumber(d.amount_variance),
      current_amount: toNumber(d.current_amount),
      billing_contact: exportState.billingContactNames(d).join(', '),
      tpa_address: d.tpa_address,
      filevine_id: d.tpa_filevine_id,
    })[key]

    // Every row matching the current filter/search/sort, not just this page.
    const downloadExcel = async () => {
      exportingExcel.value = true
      try {
        const inner = await fetchDrafts(lastListingParams)
        downloadTableExcel({
          columns: GenerateInvoicesTableColumns.value,
          rows: withIds(Array.isArray(inner.data) ? inner.data : []),
          cellValue: draftCellValue,
          sheetName: 'Draft Invoices',
          filePrefix: 'draft-invoices',
        })
      } catch (e) {
        console.error('Failed to export draft invoices:', e)
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to export. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      } finally {
        exportingExcel.value = false
      }
    }

    const handleRefresh = async () => {
      await nextTick()
      if (!dataTableRef.value) return
      dataTableRef.value.handleRefresh()
    }

    // A toggle in the Billing Contacts popup (LIQ-2711) changes the contacts
    // of every draft billed to that company, not just the row it was opened
    // from: patch them all in place (LIMOSS drafts keep their fixed contact).
    // Selected rows on other pages are patched here; the page on screen is
    // refetched, which also re-applies a Billing contact filter or sort.
    const onBillingContactsChanged = ({ companyId, emails }) => {
      if (!companyId) return
      rowsById.value = Object.fromEntries(Object.entries(rowsById.value).map(([id, d]) => [id,
        d.tpa_company_id === companyId && d.billing_type !== 'LIMOSS'
          ? { ...d, billing_contact: [...emails] }
          : d]))
      handleRefresh()
    }
    // Popup changes from either table come back through the parent.
    watch(() => props.billingContactsUpdate, (update) => {
      if (update) onBillingContactsChanged(update)
    })

    const clearSelection = () => {
      modelValue.value = []
    }

    // Switching the toggle drops any selection that's no longer visible, so
    // Approve/Export can never act on rows the user can't see.
    watch(billingTypeFilter, async () => {
      modelValue.value = modelValue.value.filter((id) => {
        const d = rowsById.value[id]
        return d && (billingTypeFilter.value === 'Both' || billingTypeOf(d) === billingTypeFilter.value)
      })
      // A filter chip on a column the toggle just hid (e.g. TPA when
      // switching to LIMOSS) would keep narrowing the rows invisibly.
      const table = dataTableRef.value
      if (table && table.filters) {
        const visibleKeys = new Set(GenerateInvoicesTableColumns.value.map(c => c.key))
        for (let i = table.filters.length - 1; i >= 0; i--) {
          if (!visibleKeys.has(table.filters[i].key.replace(/_(gt|lt)$/, ''))) table.filters.splice(i, 1)
        }
      }
      await handleRefresh()
    })

    // Drag-to-resize column headers, same as the Invoicing tab's tables.
    // Re-run on every update: the toggle adds/removes header cells. Already
    // wired headers are skipped (see enableColumnResize).
    onUpdated(() => nextTick(() => enableColumnResize(instance.proxy.$el)))

    // ── Approval ─────────────────────────────────────────────────────────

    // Matches a bulk-approve result/error entry back to the original draft
    // group it came from — Lincoln's results[]/errors[] entries don't carry
    // enough fields alone (results[] has no currency_type/billing_month/
    // billing_year; errors[] nests the original sent item under `.item`),
    // so both are matched via this same composite key instead of raw
    // array position (which partial failures would throw off).
    const isSameDraftGroup = (a, b) =>
      a.billing_type === b.billing_type &&
      a.tpa_company_id === b.tpa_company_id &&
      a.underwriter_company_id === b.underwriter_company_id &&
      (a.syndicate_number || null) === (b.syndicate_number || null) &&
      a.currency_type === b.currency_type &&
      a.billing_month === b.billing_month &&
      a.billing_year === b.billing_year

    const approveSelected = async () => {
      if (!selectedDraftObjects.value.length) return

      const blocked = selectedDraftObjects.value.filter(d => draftMissingRequirements(d).length > 0)
      if (blocked.length) {
        const reasons = new Set()
        blocked.forEach(d => draftMissingRequirements(d).forEach(r => reasons.add(r)))
        proxy.$bvToast && proxy.$bvToast.toast(
          `${blocked.length} selected invoice(s) cannot be approved — missing ${[...reasons].join(', ')}. Add these on the TPA's company profile / billing contacts first.`,
          { title: 'Missing required info', variant: 'danger', autoHideDelay: 5000 }
        )
        return
      }

      approving.value = true
      // Snapshot before clearSelection() runs below — selectedDraftObjects
      // is a computed off modelValue, so it goes empty the moment the
      // selection is cleared.
      const targetDrafts = selectedDraftObjects.value
      try {
        const invoices = targetDrafts.map(draft => ({
          tpa_company_id: draft.tpa_company_id,
          underwriter_company_id: draft.underwriter_company_id,
          currency_type: draft.currency_type,
          billing_month: draft.billing_month,
          billing_year: draft.billing_year,
          // Identifies WHICH group is being approved. A Market group and a
          // LIMOSS group can share tpa/underwriter/currency/period, so without
          // this both sets of charges land on one invoice.
          billing_type: draft.billing_type,
          // A LIMOSS group is split by syndicate too, so approving one
          // syndicate's draft only links that syndicate's own charges.
          // Always null for Market.
          syndicate_number: draft.syndicate_number,
          dispatch_mode: 'auto',
          invoice_reference: exportState.generateRef(draft),
          fv_project_id: draft.tpa_filevine_id,
        }))

        // Lincoln approves items serially (one draft-charges query + insert
        // each), and the whole request has to finish inside LimeIQ's 30s
        // proxy/gunicorn timeout — a large selection sent in one go timed
        // out, the FE never saw the created invoices, and so never
        // generated/uploaded their PDF/XLS. Sending small batches keeps each
        // request well inside that window, and uploading each batch's
        // documents straight away means a later batch failing can't strand
        // invoices that were already approved.
        let approvedCount = 0
        let failedBatches = 0
        for (let start = 0; start < invoices.length; start += BULK_APPROVE_BATCH_SIZE) {
          const batchInvoices = invoices.slice(start, start + BULK_APPROVE_BATCH_SIZE)
          const batchDrafts = targetDrafts.slice(start, start + BULK_APPROVE_BATCH_SIZE)

          let results, errors
          try {
            const resp = await InvoicingService.bulkApproveInvoices(batchInvoices)
            // Lincoln's own {status, results, errors} body gets wrapped again
            // by the proxy layer into {status, data: {...}} — same envelope
            // every other call site in this app unwraps via resp.data.data.
            ;({ results, errors } = (resp.data && resp.data.data) || {})
          } catch (e) {
            console.error('Approval batch failed:', batchInvoices, e)
            failedBatches += 1
            continue
          }

          if (errors && errors.length) {
            console.warn('Approval errors:', errors)
          }

          if (results && results.length) {
            approvedCount += results.length
            const failedItems = (errors || []).map(e => e.item)
            const succeededDrafts = batchDrafts.filter((draft, i) =>
              !failedItems.some(failed => isSameDraftGroup(failed, batchInvoices[i])))
            if (succeededDrafts.length === results.length) {
              const newlyApproved = results.map((result, i) => ({ ...succeededDrafts[i], ...result }))
              await exportState.generateAndUploadDocuments(newlyApproved)
            }
          }
        }

        if (approvedCount) {
          proxy.$bvToast && proxy.$bvToast.toast(
            `${approvedCount} invoice(s) approved successfully.`,
            { title: 'Approved', variant: 'success', autoHideDelay: 3000 }
          )
        }
        if (failedBatches) {
          proxy.$bvToast && proxy.$bvToast.toast(
            'Some invoices could not be approved. Refresh and check the remaining drafts before retrying.',
            { title: 'Error', variant: 'danger', autoHideDelay: 6000 }
          )
        }

        clearSelection()
        rowsById.value = {}
        await handleRefresh()
        emit('changed')
      } catch (e) {
        console.error('Approval failed:', e)
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to approve invoices. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      } finally {
        approving.value = false
      }
    }

    const tableTbodyTrClass = () => ''

    return {
      exportingExcel,
      downloadExcel,
      exportState,
      GenerateInvoicesTableColumns,
      onBillingContactsChanged,

      draftError,
      total,
      loaded,
      totalUnfiltered,

      modelValue,
      dataTableRef,
      billingTypeOptions,
      billingTypeFilter,
      billingTypeModel,
      selectedDraftObjects,
      hasMissingRequirements,
      missingRequirementsSummary,
      firstAddressLine,
      truncateFilevine,

      approving,

      itemsProvider,
      tableTbodyTrClass,
      handleRefresh,
      clearSelection,

      approveSelected,
    }
  },
})
</script>

<style scoped>
.gi-root { padding: 0; }

.gi-root >>> .table {
  font-size: 11px;
  table-layout: fixed;
}
.gi-root >>> .table th:first-child,
.gi-root >>> .table td:first-child {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.gi-root >>> .table td > div {
  padding: 8px 10px!important;
}
.gi-root >>> .table td:first-child > div {
  padding: 0!important;
}
.gi-root >>> .table th,
.gi-root >>> .table td {
  font-size: 11px!important;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
}
.gi-root >>> .table thead th {
  font-size: 10px!important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 6px;
}
.gi-root >>> .table .custom-checkbox {
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
}
.gi-root >>> .card .card-body {
    padding: 0 1.5rem!important;
}
.gi-root >>> .container {
    padding: 0!important;
    max-width: initial!important;
}

.gi-table-label-row {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;
}
.gi-table-label-row .gi-table-label { margin-bottom: 0; }
.gi-table-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #8898aa; margin-bottom: 8px;
}

.gi-billing-toggle { vertical-align: middle; }

.gi-billing-toggle >>> .btn { font-size: 11px; padding: 3px 12px; }

.gi-action-bar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; flex-wrap: wrap; gap: 8px;
}
.gi-action-bar__count { font-size: 13px; font-weight: 600; color: #32325d; }
.gi-action-bar__buttons { display: flex; gap: 6px; }

.gi-approve-btn { display: inline-flex; align-items: center; }
/* The (i) icon lives inside the approve button but @click.stop keeps a
   click on it from also bubbling up and firing the approve action. */
.gi-info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  cursor: help; font-size: 10px;
}
.gi-info-icon:hover, .gi-info-icon:focus { background: rgba(255, 255, 255, 0.25); outline: none; }

.gi-progress-bar {
  background: #fff; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 10px 16px; margin-bottom: 12px;
}
.gi-progress-bar__label {
  font-size: 12px; color: #32325d; font-weight: 600; margin-bottom: 6px;
}
.gi-progress-bar__track {
  width: 100%; height: 8px; border-radius: 4px;
  background: #e9ecef; overflow: hidden;
}
.gi-progress-bar__fill {
  height: 100%; background: #1c4a50; border-radius: 4px;
  transition: width 0.2s ease;
}

.gi-ref { color: #1c4a50; font-size: 10px; font-family: monospace; }
.gi-num { display: inline-block; font-size: 11px; }
.gi-amount { font-weight: 600; color: #1c4a50; font-size: 11px; }
.gi-contact { font-size: 10px; color: #525f7f; white-space: nowrap; text-wrap: inherit; }

/* Address & FileVine cells only — keep the (already JS-truncated) value on
   a single line so the ellipsis sits right after the text instead of
   wrapping. Not applied to the billing-contact cell, which needs its
   "+N" more-link to stay visible. */
.gi-truncate-cell {
  display: inline-block; max-width: 100%; vertical-align: bottom;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gi-contact-more {
  color: #1c4a50; font-weight: 600; margin-left: 4px;
  text-decoration: none; cursor: pointer;
}
.gi-contact-more:hover { text-decoration: underline; }
.gi-contact-cell { display: inline-flex; align-items: center; gap: 4px; max-width: 100%; }
.gi-contact-menu {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border: none; border-radius: 4px; padding: 0;
  background: transparent; color: #8898aa; font-size: 12px; cursor: pointer;
  flex-shrink: 0; line-height: 1;
}
.gi-contact-menu:hover { background: #f0f0f0; color: #1c4a50; }
.gi-variance-pct { font-size: 9px; color: #8898aa; margin-left: 2px; }
.gi-alert-flag { color: #e67e22; font-weight: 700; margin-left: 2px; font-size: 10px; }
.gi-td--alert { color: #e67e22; font-weight: 600; }

.gi-status-badge {
  display: inline-block; padding: 1px 6px; border-radius: 10px;
  font-size: 9px; font-weight: 600; background: #e9ecef; color: #525f7f;
  white-space: nowrap;
}
.gi-status-badge--draft { background: #e9ecef; color: #525f7f; }
.gi-status-badge--updated { background: #fff3cd; color: #856404; }

.gi-preview-btn { font-size: 10px; padding: 2px 7px; margin-left: 3px; }

.gi-loading, .gi-empty { padding: 40px; text-align: center; color: #8898aa; font-size: 13px; }
.gi-error { padding: 16px; text-align: center; color: #dc3545; font-size: 13px; }

</style>
